import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MdAssessment,
  MdAccessTime,
  MdCalendarToday,
  MdBarChart,
  MdBuild,
  MdLocationOn,
  MdImage,
} from "react-icons/md";
// eslint-disable-next-line import/no-duplicates
import { format } from "date-fns";
// eslint-disable-next-line import/no-duplicates
import ptBR from "date-fns/locale/pt-BR";
import { api } from "../../services/api";

interface RankingItem {
  id: string;
  name: string;
  count: number;
}

interface CrossTableRow {
  sector_id: string;
  sector_name: string;
  [equipment_id: string]: string | number;
}

interface EquipmentDetail {
  id: string;
  name: string;
  grade: string;
}

interface SectorDetail {
  sector_id: string;
  sector_name: string;
  observation: string;
  images: Array<{ id: string; file_name: string }>;
  equipments: EquipmentDetail[];
}

interface ReportDetail {
  report_id: string;
  report_code: number;
  report_date: string;
  sectors: SectorDetail[];
}

interface ManagerialReport {
  costumer: {
    id: string;
    name: string;
    address: string;
  };
  totalReports: number;
  averageVisitTimeMinutes: number;
  averageDaysBetweenReports: number;
  topSectors: RankingItem[];
  topEquipments: RankingItem[];
  crossTable: {
    equipments: Array<{ id: string; name: string }>;
    rows: CrossTableRow[];
  };
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function MedalBadge({ position }: { position: number }) {
  const colors = ["bg-yellow-400", "bg-gray-300", "bg-amber-600"];
  const labels = ["1º", "2º", "3º"];
  return (
    <span
      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold ${colors[position]}`}
    >
      {labels[position]}
    </span>
  );
}

export function ManagerialReportPrint() {
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState<ManagerialReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailedView, setDetailedView] = useState<ReportDetail[]>([]);

  const costumer_id = searchParams.get("costumer_id");
  const begin = searchParams.get("begin");
  const end = searchParams.get("end");
  const sector_ids = searchParams.get("sector_ids");

  useEffect(() => {
    async function loadReport() {
      if (!costumer_id || !begin || !end) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get("/techVisitReport/managerial-report", {
          params: { costumer_id, begin, end },
        });
        setReport(response.data);

        // Carrega detailed view se tiver sector_ids
        if (sector_ids) {
          const detailResponse = await api.get("/techVisitReport/detailed-view", {
            params: {
              costumer_id,
              begin,
              end,
              sector_ids,
            },
          });
          setDetailedView(detailResponse.data);
        }
      } catch (error) {
        console.error("Erro ao carregar relatório:", error);
      } finally {
        setLoading(false);
      }
    }

    loadReport();
  }, [costumer_id, begin, end, sector_ids]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0085BD] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando relatório...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Erro ao carregar relatório.</p>
      </div>
    );
  }

  const beginDate = begin ? new Date(begin) : null;
  const endDate = end ? new Date(end) : null;

  function getGradeLabel(grade: string): string {
    const labels: Record<string, string> = {
      O: "Ótimo",
      B: "Bom",
      R: "Revisado",
      S: "Substituído",
    };
    return labels[grade] || grade;
  }

  function getGradeColor(grade: string): string {
    const colors: Record<string, string> = {
      O: "bg-green-100 text-green-700",
      B: "bg-blue-100 text-blue-700",
      R: "bg-yellow-100 text-yellow-700",
      S: "bg-red-100 text-red-700",
    };
    return colors[grade] || "bg-gray-100 text-gray-700";
  }

  return (
    <div className="bg-white p-8 max-w-[1200px] mx-auto print-container">
      <style>{`
        * {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-container {
            page-break-inside: avoid;
          }
        }
      `}</style>

      {/* Header */}
      <div className="mb-8 pb-6 border-b-2 border-gray-200">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Relatório Gerencial
        </h1>
        <p className="text-gray-600">Compilado de visitas técnicas</p>
      </div>

      {/* Cliente */}
      <div className="mb-8 bg-gray-50 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#0085BD]/10 flex items-center justify-center flex-shrink-0">
            <MdLocationOn size={24} className="text-[#0085BD]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {report.costumer.name}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {report.costumer.address}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Período:{" "}
              <span className="font-medium text-gray-700">
                {beginDate && format(beginDate, "dd/MM/yyyy", { locale: ptBR })} –{" "}
                {endDate && format(endDate, "dd/MM/yyyy", { locale: ptBR })}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-[#0085BD]/5 rounded-xl p-5 border border-[#0085BD]/20">
          <div className="flex items-center gap-3 mb-2">
            <MdAssessment size={24} className="text-[#0085BD]" />
            <p className="text-xs text-gray-600 font-medium">
              Relatórios no período
            </p>
          </div>
          <p className="text-3xl font-bold text-gray-800">
            {report.totalReports}
          </p>
        </div>

        <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
          <div className="flex items-center gap-3 mb-2">
            <MdAccessTime size={24} className="text-emerald-600" />
            <p className="text-xs text-gray-600 font-medium">
              Tempo médio de visita
            </p>
          </div>
          <p className="text-3xl font-bold text-gray-800">
            {formatMinutes(report.averageVisitTimeMinutes)}
          </p>
        </div>

        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
          <div className="flex items-center gap-3 mb-2">
            <MdCalendarToday size={24} className="text-purple-600" />
            <p className="text-xs text-gray-600 font-medium">
              Média dias entre visitas
            </p>
          </div>
          <p className="text-3xl font-bold text-gray-800">
            {report.averageDaysBetweenReports > 0
              ? `${report.averageDaysBetweenReports} dias`
              : "—"}
          </p>
        </div>
      </div>

      {/* Rankings */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Top Setores */}
        <div className="border border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
            <MdBarChart size={20} className="text-[#0085BD]" />
            <h3 className="font-bold text-gray-800 text-sm">
              Top 3 Setores — Equipamentos Revisados
            </h3>
          </div>
          {report.topSectors.length === 0 ? (
            <p className="text-sm text-gray-400">
              Nenhum equipamento revisado no período.
            </p>
          ) : (
            <ol className="space-y-3">
              {report.topSectors.map((item, idx) => (
                <li key={item.id} className="flex items-center gap-3">
                  <MedalBadge position={idx} />
                  <span className="flex-1 text-sm text-gray-700 font-medium">
                    {item.name}
                  </span>
                  <span className="text-sm font-bold text-[#0085BD]">
                    {item.count}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Top Equipamentos */}
        <div className="border border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
            <MdBuild size={20} className="text-[#0085BD]" />
            <h3 className="font-bold text-gray-800 text-sm">
              Top 3 Equipamentos — Mais Revisados
            </h3>
          </div>
          {report.topEquipments.length === 0 ? (
            <p className="text-sm text-gray-400">
              Nenhum equipamento revisado no período.
            </p>
          ) : (
            <ol className="space-y-3">
              {report.topEquipments.map((item, idx) => (
                <li key={item.id} className="flex items-center gap-3">
                  <MedalBadge position={idx} />
                  <span className="flex-1 text-sm text-gray-700 font-medium">
                    {item.name}
                  </span>
                  <span className="text-sm font-bold text-[#0085BD]">
                    {item.count}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Tabela cruzada */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-800">
            Equipamentos Revisados por Setor
          </h3>
        </div>
        {report.crossTable.rows.length === 0 ||
        report.crossTable.equipments.length === 0 ? (
          <div className="p-6">
            <p className="text-sm text-gray-400">
              Nenhum dado disponível para a tabela.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b border-gray-200">
                    Setor
                  </th>
                  {report.crossTable.equipments.map((eq) => (
                    <th
                      key={eq.id}
                      className="px-4 py-3 text-center font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
                    >
                      {eq.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.crossTable.rows.map((row, rowIdx) => (
                  <tr
                    key={row.sector_id}
                    className={rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-3 font-medium text-gray-700 border-b border-gray-100">
                      {row.sector_name}
                    </td>
                    {report.crossTable.equipments.map((eq) => {
                      const val = row[eq.id] as number;
                      return (
                        <td
                          key={eq.id}
                          className="px-4 py-3 text-center border-b border-gray-100"
                        >
                          {val > 0 ? (
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0085BD]/10 text-[#0085BD] font-bold text-xs">
                              {val}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Visão Detalhada */}
      {detailedView.length > 0 && (
        <div className="mt-8 border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="font-bold text-gray-800">Visão Detalhada</h3>
          </div>
          <div className="p-6 space-y-6">
            {detailedView.map((reportDetail) => (
              <div
                key={reportDetail.report_id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Header do Relatório */}
                <div className="bg-[#0085BD] text-white px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold">
                        Relatório #{reportDetail.report_code}
                      </span>
                      <p className="text-xs opacity-90 mt-0.5">
                        {format(new Date(reportDetail.report_date), "dd 'de' MMMM 'de' yyyy", {
                          locale: ptBR,
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Setores do Relatório */}
                <div className="p-4 space-y-4">
                  {reportDetail.sectors.map((sector) => (
                    <div
                      key={sector.sector_id}
                      className="bg-gray-50 rounded-lg p-4 space-y-3"
                    >
                      {/* Nome do Setor */}
                      <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                        <MdLocationOn className="text-[#0085BD]" size={18} />
                        <h4 className="font-semibold text-gray-800">
                          {sector.sector_name}
                        </h4>
                      </div>

                      {/* Observação */}
                      {sector.observation && (
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">
                            Observação
                          </p>
                          <p className="text-sm text-gray-700">
                            {sector.observation}
                          </p>
                        </div>
                      )}

                      {/* Equipamentos */}
                      {sector.equipments.length > 0 && (
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-1">
                            <MdBuild size={14} />
                            Equipamentos
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {sector.equipments.map((eq) => (
                              <span
                                key={eq.id}
                                className={`px-3 py-1 rounded-full text-xs font-medium ${getGradeColor(
                                  eq.grade
                                )}`}
                              >
                                {eq.name} — {getGradeLabel(eq.grade)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Imagens */}
                      {sector.images.length > 0 && (
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-1">
                            <MdImage size={14} />
                            Imagens ({sector.images.length})
                          </p>
                          <div className="grid grid-cols-4 gap-2">
                            {sector.images.map((img) => (
                              <div
                                key={img.id}
                                className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200"
                              >
                                <img
                                  src={`https://app-luvilimp.s3.amazonaws.com/techVisitReportSectorImages/${img.file_name}`}
                                  alt="Imagem do setor"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-500">
          Relatório gerado em {format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
        </p>
      </div>
    </div>
  );
}
